import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { supabase } from '@/lib/supabase';
import { calculateDiscount, formatAmount, validateDiscountCode } from '@/lib/stax';
import {
  createCustomer,
  enrollMembership,
  searchCustomer,
  type EnrollmentRequest,
  type SearchCustomerResponse,
} from '@/lib/memberships-api-client';
import { useStax } from '@/hooks/useStax';

export interface CompanyOption {
  id: string;
  name: string;
  brand_key: string | null;
}

interface MembershipPlan {
  id: string;
  plan_code: string;
  plan_name: string;
  base_price_monthly_1_pm: number;
  base_price_annual_1_pm: number;
  base_price_monthly_2_pm: number;
  base_price_annual_2_pm: number;
  additional_system_monthly_1_pm: number;
  additional_system_annual_1_pm: number;
  additional_system_monthly_2_pm: number;
  additional_system_annual_2_pm: number;
}

interface DiscountCode {
  id: string;
  code: string;
  code_name: string;
  discount_type: 'percentage' | 'fixed_amount';
  discount_value: number;
  plan_codes: string[] | null;
  applies_to_billing: string[] | null;
  valid_from: string | null;
  valid_until: string | null;
  is_active: boolean;
  description: string;
  terms_conditions: string;
}

type Step = 'company' | 'customer' | 'plan' | 'payment';

type CustomerMatch = SearchCustomerResponse['customers'][number];

export function AdminCreateMembershipModal({
  isOpen,
  onClose,
  companies,
  defaultCompanyId,
  onCreated,
}: {
  isOpen: boolean;
  onClose: () => void;
  companies: CompanyOption[];
  defaultCompanyId?: string;
  onCreated: (membershipId: string) => void;
}) {
  const [step, setStep] = useState<Step>('company');
  const [error, setError] = useState('');
  const [isBusy, setIsBusy] = useState(false);

  // Payment form ref for Stax tokenization
  const paymentFormRef = useRef<HTMLFormElement>(null);
  const { isStaxReady, cardFormStatus, staxError, setBrandKey, tokenizePaymentMethod } = useStax();

  // Company
  const [companyId, setCompanyId] = useState('');

  const company = useMemo(() => {
    return companies.find((c) => c.id === companyId) || null;
  }, [companies, companyId]);

  const successwareCompany = company?.brand_key || null;

  // Customer search/create
  const [phone, setPhone] = useState('');
  const [matches, setMatches] = useState<CustomerMatch[]>([]);
  const [selectedMatchIndex, setSelectedMatchIndex] = useState(0);
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('FL');
  const [zip, setZip] = useState('');

  const [successwareCustomerId, setSuccesswareCustomerId] = useState('');
  const [successwareServiceAccountId, setSuccesswareServiceAccountId] = useState('');
  const [successwareLocationId, setSuccesswareLocationId] = useState('');

  // Plan selection
  const [planId, setPlanId] = useState('');
  const [billingFrequency, setBillingFrequency] = useState<'monthly' | 'annual'>('monthly');
  const [pmFrequency, setPmFrequency] = useState<1 | 2>(1);
  const [systemsCount, setSystemsCount] = useState(1);

  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState<DiscountCode | null>(null);
  const [discountError, setDiscountError] = useState('');

  // Payment
  const [cardMonth, setCardMonth] = useState('');
  const [cardYear, setCardYear] = useState('');

  // Reset state on open
  useEffect(() => {
    if (!isOpen) return;

    const initialCompanyId = defaultCompanyId || '';

    setStep(initialCompanyId ? 'customer' : 'company');
    setCompanyId(initialCompanyId);
    setError('');
    setIsBusy(false);

    setPhone('');
    setMatches([]);
    setSelectedMatchIndex(0);
    setShowNewCustomerForm(false);

    setFirstName('');
    setLastName('');
    setEmail('');
    setAddress('');
    setCity('');
    setState('FL');
    setZip('');

    setSuccesswareCustomerId('');
    setSuccesswareServiceAccountId('');
    setSuccesswareLocationId('');

    setPlanId('');
    setBillingFrequency('monthly');
    setPmFrequency(1);
    setSystemsCount(1);

    setDiscountCode('');
    setAppliedDiscount(null);
    setDiscountError('');

    setCardMonth('');
    setCardYear('');
  }, [isOpen, defaultCompanyId]);

  const { data: plans, isLoading: plansLoading } = useQuery({
    enabled: isOpen && !!companyId,
    queryKey: ['admin-membership-plans', companyId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('membership_plans')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw error;
      return (data || []) as MembershipPlan[];
    },
    staleTime: 60_000,
  });

  const selectedPlan = useMemo(() => {
    return (plans || []).find((p) => p.id === planId) || null;
  }, [plans, planId]);

  function calculateBasePrice(plan: MembershipPlan): number {
    let basePrice: number;

    if (pmFrequency === 1) {
      basePrice = billingFrequency === 'monthly' ? plan.base_price_monthly_1_pm : plan.base_price_annual_1_pm;
    } else {
      basePrice = billingFrequency === 'monthly' ? plan.base_price_monthly_2_pm : plan.base_price_annual_2_pm;
    }

    if (systemsCount > 1) {
      let additionalPrice: number;

      if (pmFrequency === 1) {
        additionalPrice =
          billingFrequency === 'monthly' ? plan.additional_system_monthly_1_pm : plan.additional_system_annual_1_pm;
      } else {
        additionalPrice =
          billingFrequency === 'monthly' ? plan.additional_system_monthly_2_pm : plan.additional_system_annual_2_pm;
      }

      basePrice += additionalPrice * (systemsCount - 1);
    }

    return basePrice;
  }

  const pricing = useMemo(() => {
    if (!selectedPlan) {
      return { basePrice: 0, discountAmount: 0, finalPrice: 0 };
    }

    const basePrice = calculateBasePrice(selectedPlan);
    if (!appliedDiscount) {
      return { basePrice, discountAmount: 0, finalPrice: basePrice };
    }

    const discountAmount = calculateDiscount(basePrice, appliedDiscount.discount_type, appliedDiscount.discount_value);
    return {
      basePrice,
      discountAmount,
      finalPrice: Math.max(0, basePrice - discountAmount),
    };
  }, [selectedPlan, billingFrequency, pmFrequency, systemsCount, appliedDiscount]);

  async function handleSearchCustomer() {
    setError('');
    setDiscountError('');

    if (!companyId) {
      setError('Select a company first.');
      return;
    }

    if (!successwareCompany) {
      setError(`Company ${companyId} is missing brand_key; cannot call SuccessWare.`);
      return;
    }

    const cleanedPhoneRaw = phone.replace(/\D/g, '');
    let cleanedPhone = cleanedPhoneRaw;
    if (cleanedPhone.length === 11 && cleanedPhone.startsWith('1')) cleanedPhone = cleanedPhone.slice(1);
    if (cleanedPhone.length > 10) cleanedPhone = cleanedPhone.slice(-10);

    if (cleanedPhone.length < 10) {
      setError('Enter a full 10-digit phone number for customer lookup.');
      return;
    }

    setIsBusy(true);
    try {
      const {
        data: { session },
        error: sessionErr,
      } = await supabase.auth.getSession();
      if (sessionErr) throw sessionErr;

      const token = session?.access_token;
      if (!token) {
        throw new Error('Missing auth token. Please sign in again.');
      }

      // Use the same drilldown path as /admin/customers so we get *all* accounts/locations.
      const resp = await fetch('/api/admin/customers/phone-hierarchy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          phone: cleanedPhone,
          company_id: companyId,
        }),
      });

      let payload: any = null;
      try {
        payload = await resp.json();
      } catch {
        // ignore
      }

      if (!resp.ok) {
        throw new Error(payload?.error || payload?.message || `Request failed (${resp.status})`);
      }

      const billingAccounts = Array.isArray(payload?.billing_accounts) ? payload.billing_accounts : [];

      const splitLabel = (label: string | null) => {
        const raw = String(label || '').trim();
        if (!raw) return { firstName: '', lastName: '' };
        const parts = raw.split(/\s+/).filter(Boolean);
        if (parts.length <= 1) return { firstName: parts[0] || '', lastName: '' };
        return { firstName: parts[0] || '', lastName: parts.slice(1).join(' ') };
      };

      const baseMatches: CustomerMatch[] = [];
      const locationIds: string[] = [];

      for (const acct of billingAccounts) {
        const billingCustomerId = typeof acct?.billing_customer_id === 'string' ? acct.billing_customer_id : '';
        const label = typeof acct?.label === 'string' ? acct.label : null;
        const { firstName, lastName } = splitLabel(label);

        const locations = Array.isArray(acct?.locations) ? acct.locations : [];
        for (const loc of locations) {
          const locationId = typeof loc?.location_id === 'string' ? loc.location_id : '';
          if (!locationId) continue;

          const serviceAccountId = typeof loc?.service_account_id === 'string' ? loc.service_account_id : '';

          const address1 = typeof loc?.address1 === 'string' ? loc.address1 : '';
          const cityFromLoc = typeof loc?.city === 'string' ? loc.city : '';
          const stateFromLoc = typeof loc?.state === 'string' ? loc.state : '';
          const zipFromLoc = typeof loc?.zip_code === 'string' ? loc.zip_code : '';

          baseMatches.push({
            customerId: billingCustomerId,
            serviceAccountId,
            locationId,
            firstName,
            lastName,
            email: '',
            phone: cleanedPhone,
            address: address1,
            city: cityFromLoc,
            state: stateFromLoc,
            zip: zipFromLoc,
          });
          locationIds.push(locationId);
        }
      }

      // Enrich the options with the same sw_customers details that /admin/customers renders.
      let enrichedMatches = baseMatches;
      const uniqueLocationIds = [...new Set(locationIds)].filter(Boolean);

      if (uniqueLocationIds.length > 0) {
        const { data: swRows, error: swErr } = await supabase
          .from('sw_customers')
          .select('id, first_name, last_name, email, phone_number, address1, city, state, zip')
          .eq('is_active', true)
          .eq('company_id', companyId)
          .in('id', uniqueLocationIds);

        if (!swErr && Array.isArray(swRows)) {
          const byId = new Map<string, any>(swRows.map((r: any) => [String(r.id), r]));
          enrichedMatches = baseMatches.map((m) => {
            const row = byId.get(m.locationId) || null;
            if (!row) return m;

            return {
              ...m,
              firstName: typeof row.first_name === 'string' ? row.first_name : m.firstName,
              lastName: typeof row.last_name === 'string' ? row.last_name : m.lastName,
              email: typeof row.email === 'string' ? row.email : m.email,
              phone: typeof row.phone_number === 'string' ? row.phone_number.replace(/\D/g, '') : m.phone,
              address: typeof row.address1 === 'string' ? row.address1 : m.address,
              city: typeof row.city === 'string' ? row.city : m.city,
              state: typeof row.state === 'string' ? row.state : m.state,
              zip: typeof row.zip === 'string' ? row.zip : m.zip,
            };
          });
        }
      }

      setMatches(enrichedMatches);
      setSelectedMatchIndex(0);

      if (enrichedMatches.length > 0) {
        applyCustomerMatch(enrichedMatches[0]);
        setShowNewCustomerForm(false);
      } else {
        setShowNewCustomerForm(true);
      }
    } catch (e: any) {
      setError(e?.message || 'Customer lookup failed.');
    } finally {
      setIsBusy(false);
    }
  }

  function applyCustomerMatch(match: CustomerMatch) {
    setSuccesswareCustomerId(match.customerId);
    setSuccesswareServiceAccountId(match.serviceAccountId);
    setSuccesswareLocationId(match.locationId);

    if (match.firstName) setFirstName(match.firstName);
    if (match.lastName) setLastName(match.lastName);
    if (match.email) setEmail(match.email);

    setPhone(match.phone || phone);
    if (match.address) setAddress(match.address);
    if (match.city) setCity(match.city);
    if (match.state) setState(match.state);
    if (match.zip) setZip(match.zip);
  }

  async function handleCreateCustomer() {
    setError('');

    if (!companyId) {
      setError('Select a company first.');
      return;
    }

    if (!successwareCompany) {
      setError(`Company ${companyId} is missing brand_key; cannot call SuccessWare.`);
      return;
    }

    if (!firstName || !lastName || !email || !phone || !address || !city || !state || !zip) {
      setError('Fill in first name, last name, email, phone, and full address.');
      return;
    }

    setIsBusy(true);
    try {
      const cleanedPhone = phone.replace(/\D/g, '');

      const res = await createCustomer({
        company: successwareCompany,
        customer: {
          firstName,
          lastName,
          email,
          phone: cleanedPhone,
        },
        location: {
          address1: address,
          city,
          state,
          zipCode: zip,
        },
      });

      setSuccesswareCustomerId(res.customerId);
      setSuccesswareServiceAccountId(res.serviceAccountId);
      setSuccesswareLocationId(res.locationId);

      setStep('plan');
    } catch (e: any) {
      setError(e?.message || 'Failed to create customer.');
    } finally {
      setIsBusy(false);
    }
  }

  async function handleApplyDiscount() {
    setDiscountError('');

    if (!discountCode) return;
    if (!selectedPlan) {
      setDiscountError('Select a plan first.');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('discount_codes')
        .select('*')
        .ilike('code', discountCode.trim())
        .eq('is_active', true)
        .maybeSingle();

      if (error) throw error;
      if (!data) {
        setDiscountError('Invalid discount code');
        return;
      }

      const validation = validateDiscountCode(data as DiscountCode, selectedPlan.plan_code, billingFrequency);
      if (!validation.valid) {
        setDiscountError(validation.reason || 'Invalid discount code');
        return;
      }

      setAppliedDiscount(data as DiscountCode);
      setDiscountError('');
    } catch {
      setDiscountError('Error validating discount code');
    }
  }

  async function handleCreateMembership() {
    setError('');

    if (!companyId) {
      setError('Select a company first.');
      return;
    }

    if (!selectedPlan) {
      setError('Select a plan.');
      return;
    }

    if (!successwareCustomerId || !successwareServiceAccountId || !successwareLocationId) {
      setError('Customer must be linked to SuccessWare (search or create the customer first).');
      return;
    }

    if (!email || !firstName || !lastName || !phone) {
      setError('Customer email, first name, last name, and phone are required.');
      return;
    }

    if (!address || !city || !state || !zip) {
      setError('Customer address is required for enrollment.');
      return;
    }

    if (!isStaxReady || cardFormStatus !== 'mounted') {
      setError('Payment system is still loading. Please wait a moment and try again.');
      return;
    }

    if (!cardMonth || !cardYear) {
      setError('Enter card expiration month and year.');
      return;
    }

    if (!paymentFormRef.current) {
      setError('Payment form is not ready. Please refresh and try again.');
      return;
    }

    setIsBusy(true);
    try {
      // Set brand key before tokenizing
      if (successwareCompany) setBrandKey(successwareCompany);

      const paymentToken = await tokenizePaymentMethod({
        form: paymentFormRef.current,
        extraDetails: {
          firstname: firstName,
          lastname: lastName,
          email,
          phone,
          address_1: address,
          address_city: city,
          address_state: state,
          address_zip: zip,
        },
        brandKey: successwareCompany,
      });

      // membership_plans pricing columns are stored as integer cents.
      const basePriceCents = Math.round(pricing.basePrice);
      const discountAmountCents = Math.round(pricing.discountAmount);
      const finalPriceCents = Math.round(pricing.finalPrice);

      const payload: EnrollmentRequest = {
        company_id: companyId,
        enrollment_source: 'admin',
        plan_id: selectedPlan.id,
        plan_code: selectedPlan.plan_code,
        plan_name: selectedPlan.plan_name,
        billing_frequency: billingFrequency,
        tune_ups_per_year: pmFrequency,
        systems_count: systemsCount,
        base_price_cents: basePriceCents,
        discount_code_id: appliedDiscount?.id,
        discount_amount_cents: appliedDiscount ? discountAmountCents : undefined,
        final_price_cents: finalPriceCents,
        customer: {
          email,
          first_name: firstName,
          last_name: lastName,
          phone,
          address,
          city,
          state,
          zip,
        },
        successware: {
          customer_id: successwareCustomerId,
          service_account_id: successwareServiceAccountId,
          location_id: successwareLocationId,
        },
        payment_token: paymentToken,
        terms_accepted: {
          timestamp: new Date().toISOString(),
          terms_version: 'admin',
        },
      };

      const result = await enrollMembership(payload);
      onCreated(result.membership_id);
    } catch (e: any) {
      setError(e?.message || 'Failed to create membership.');
    } finally {
      setIsBusy(false);
    }
  }

  function renderFooter() {
    if (step === 'company') {
      return (
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setError('');
              if (!companyId) {
                setError('Select a company first.');
                return;
              }
              setStep('customer');
            }}
          >
            Next
          </Button>
        </div>
      );
    }

    if (step === 'customer') {
      return (
        <div className="flex justify-between gap-2">
          <Button variant="secondary" onClick={() => setStep('company')}>
            Back
          </Button>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
            {showNewCustomerForm ? (
              <Button variant="primary" disabled={isBusy} onClick={handleCreateCustomer}>
                {isBusy ? 'Creating…' : 'Create customer'}
              </Button>
            ) : (
              <Button
                variant="primary"
                disabled={isBusy || !successwareCustomerId}
                onClick={() => {
                  setError('');
                  if (!successwareCustomerId) {
                    setError('Search/select a customer first.');
                    return;
                  }
                  setStep('plan');
                }}
              >
                Next
              </Button>
            )}
          </div>
        </div>
      );
    }

    if (step === 'plan') {
      return (
        <div className="flex justify-between gap-2">
          <Button variant="secondary" onClick={() => setStep('customer')}>
            Back
          </Button>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
            <Button
              variant="primary"
              disabled={isBusy || !selectedPlan}
              onClick={() => {
                setError('');
                if (!selectedPlan) {
                  setError('Select a plan.');
                  return;
                }
                setStep('payment');
              }}
            >
              Next
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex justify-between gap-2">
        <Button variant="secondary" onClick={() => setStep('plan')}>
          Back
        </Button>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button variant="primary" disabled={isBusy} onClick={handleCreateMembership}>
            {isBusy ? 'Creating…' : 'Create membership'}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create membership" maxWidthClassName="max-w-3xl">
      <div className="space-y-4">
        {error && <div className="text-sm text-red-600">{error}</div>}

        <div className="text-xs text-gray-500">Step: {step}</div>

        {step === 'company' && (
          <div className="space-y-2">
            <Label htmlFor="company">Company</Label>
            <Select id="company" value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
              <option value="">Select a company</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
            {companyId && !successwareCompany && (
              <p className="text-xs text-amber-600">This company is missing brand_key; SuccessWare calls will fail.</p>
            )}
          </div>
        )}

        {step === 'customer' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <div className="md:col-span-2">
                <Label htmlFor="phone">Customer phone (search)</Label>
                <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 555-5555" />
              </div>
              <Button variant="secondary" disabled={isBusy} onClick={handleSearchCustomer}>
                {isBusy ? 'Searching…' : 'Search'}
              </Button>
            </div>

            {matches.length > 0 && !showNewCustomerForm && (
              <div className="space-y-2">
                <div className="text-sm font-semibold">Matches</div>
                <Select
                  value={String(selectedMatchIndex)}
                  onChange={(e) => {
                    const idx = Number(e.target.value);
                    setSelectedMatchIndex(idx);
                    const match = matches[idx];
                    if (match) applyCustomerMatch(match);
                  }}
                >
                  {matches.map((m, idx) => (
                    <option key={`${m.customerId}-${m.locationId}`} value={String(idx)}>
                      {[
                        [m.firstName, m.lastName].filter(Boolean).join(' ') || 'Unknown',
                        m.phone,
                        m.address,
                      ]
                        .filter(Boolean)
                        .join(' • ')}
                    </option>
                  ))}
                </Select>

                <div className="flex justify-end">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setShowNewCustomerForm(true);
                      setMatches([]);
                      setSelectedMatchIndex(0);
                      setSuccesswareCustomerId('');
                      setSuccesswareServiceAccountId('');
                      setSuccesswareLocationId('');
                    }}
                  >
                    Create new customer instead
                  </Button>
                </div>
              </div>
            )}

            {showNewCustomerForm && (
              <div className="space-y-3">
                <div className="text-sm font-semibold">New customer details</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="firstName">First name</Label>
                    <Input id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="lastName">Last name</Label>
                    <Input id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="phone2">Phone</Label>
                    <Input id="phone2" value={phone} onChange={(e) => setPhone(e.target.value)} />
                  </div>
                </div>
                <div>
                  <Label htmlFor="address">Address</Label>
                  <Input id="address" value={address} onChange={(e) => setAddress(e.target.value)} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label htmlFor="city">City</Label>
                    <Input id="city" value={city} onChange={(e) => setCity(e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="state">State</Label>
                    <Input id="state" value={state} onChange={(e) => setState(e.target.value)} maxLength={2} />
                  </div>
                  <div>
                    <Label htmlFor="zip">ZIP</Label>
                    <Input id="zip" value={zip} onChange={(e) => setZip(e.target.value)} />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {step === 'plan' && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="plan">Plan</Label>
                <Select
                  id="plan"
                  value={planId}
                  onChange={(e) => {
                    setPlanId(e.target.value);
                    setAppliedDiscount(null);
                    setDiscountError('');
                  }}
                  disabled={plansLoading}
                >
                  <option value="">Select a plan</option>
                  {(plans || []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.plan_name}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="billing">Billing</Label>
                <Select
                  id="billing"
                  value={billingFrequency}
                  onChange={(e) => {
                    setBillingFrequency(e.target.value as 'monthly' | 'annual');
                    setAppliedDiscount(null);
                    setDiscountError('');
                  }}
                >
                  <option value="monthly">Monthly</option>
                  <option value="annual">Annual</option>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="pm">PM visits per year</Label>
                <Select id="pm" value={String(pmFrequency)} onChange={(e) => setPmFrequency(Number(e.target.value) as 1 | 2)}>
                  <option value="1">1</option>
                  <option value="2">2</option>
                </Select>
              </div>
              <div>
                <Label htmlFor="systems">Systems</Label>
                <Input
                  id="systems"
                  inputMode="numeric"
                  value={String(systemsCount)}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/[^0-9]/g, '');
                    const n = raw ? Number(raw) : 1;
                    setSystemsCount(Number.isFinite(n) && n > 0 ? n : 1);
                  }}
                />
              </div>
            </div>

            <div className="border rounded-lg p-3 bg-gray-50">
              <div className="flex justify-between text-sm">
                <span>Base price</span>
                <span>{formatAmount(pricing.basePrice)}</span>
              </div>
              {appliedDiscount && (
                <div className="flex justify-between text-sm text-green-700">
                  <span>Discount ({appliedDiscount.code})</span>
                  <span>-{formatAmount(pricing.discountAmount)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm font-semibold">
                <span>Final price</span>
                <span>{formatAmount(pricing.finalPrice)}</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <div className="md:col-span-2">
                <Label htmlFor="discount">Discount code (optional)</Label>
                <Input
                  id="discount"
                  value={discountCode}
                  onChange={(e) => {
                    setDiscountCode(e.target.value.toUpperCase());
                    setDiscountError('');
                  }}
                />
                {discountError && <div className="text-xs text-red-600 mt-1">{discountError}</div>}
              </div>
              <div className="flex gap-2">
                <Button variant="secondary" disabled={!discountCode || !selectedPlan} onClick={handleApplyDiscount}>
                  Apply
                </Button>
                {appliedDiscount && (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setAppliedDiscount(null);
                      setDiscountError('');
                    }}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {step === 'payment' && (
          <form ref={paymentFormRef} className="space-y-3" onSubmit={(e) => e.preventDefault()}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Stax hosted iframe for card number */}
              <div className="md:col-span-2">
                <Label>Card number</Label>
                <div
                  id="card-number"
                  className="h-10 px-3 border border-gray-300 rounded-md bg-white flex items-center"
                />
              </div>
              {/* Stax hosted iframe for CVV */}
              <div>
                <Label>CVV</Label>
                <div
                  id="card-cvv"
                  className="h-10 px-3 border border-gray-300 rounded-md bg-white flex items-center"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label htmlFor="month">Exp Month</Label>
                <Input
                  id="month"
                  name="month"
                  placeholder="MM"
                  value={cardMonth}
                  onChange={(e) => setCardMonth(e.target.value.slice(0, 2))}
                  inputMode="numeric"
                  maxLength={2}
                />
              </div>
              <div>
                <Label htmlFor="year">Exp Year</Label>
                <Input
                  id="year"
                  name="year"
                  placeholder="YYYY"
                  value={cardYear}
                  onChange={(e) => setCardYear(e.target.value.slice(0, 4))}
                  inputMode="numeric"
                  maxLength={4}
                />
              </div>
              <div className="text-xs text-gray-500 flex items-center">
                {cardFormStatus === 'loading' && 'Loading secure fields…'}
                {cardFormStatus === 'error' && staxError && <span className="text-red-500">{staxError}</span>}
                {cardFormStatus === 'mounted' && <span className="text-green-600">Payment form ready.</span>}
                {!isStaxReady && 'Loading payment system…'}
              </div>
            </div>
          </form>
        )}

        <div className="pt-2 border-t">{renderFooter()}</div>
      </div>
    </Modal>
  );
}
